import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { getUtm } from "@/lib/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALLBACK_OPTIONS = [
  "As soon as possible",
  "Weekday morning (9am–12pm)",
  "Weekday afternoon (12–5pm)",
  "Weekday evening (5–7pm)",
];

export default function LeadForm({ source, commission, extraYear1, extra5yr, testIdPrefix, dark = false }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", callback: CALLBACK_OPTIONS[0] });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        callback_time: form.callback,
        annual_commission: commission ?? null,
        extra_year1: extraYear1 ?? null,
        extra_5yr: extra5yr ?? null,
        source,
        ...getUtm(),
      });
      setStatus("success");
      toast.success("You're in — your breakdown is on its way.");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-start gap-3 py-2 ${dark ? "text-[#F9F8F6]" : ""}`}
        data-testid={`${testIdPrefix}-lead-success`}
      >
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center bg-[#FF3B30] text-white">
          <Check size={14} strokeWidth={3} />
        </span>
        <p className="text-sm leading-relaxed">
          Done. Your personalised breakdown is headed to <strong>{form.email}</strong> — and our team will call you{" "}
          {form.callback.charAt(0).toLowerCase() + form.callback.slice(1)}.
        </p>
      </motion.div>
    );
  }

  const inputCls = dark
    ? "w-full bg-transparent border border-[#F9F8F6]/25 text-[#F9F8F6] placeholder:text-[#F9F8F6]/40 px-4 py-3 text-sm outline-none focus:border-[#FF3B30] transition-colors"
    : "w-full bg-white border border-[#E5E5E0] text-[#111111] placeholder:text-[#999] px-4 py-3 text-sm outline-none focus:border-[#FF3B30] transition-colors";

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${testIdPrefix}-lead-form`}>
      <input
        required
        type="text"
        placeholder="Name"
        value={form.name}
        onChange={set("name")}
        className={inputCls}
        data-testid={`${testIdPrefix}-name-input`}
        aria-label="Name"
      />
      <input
        required
        type="email"
        placeholder="Email"
        value={form.email}
        onChange={set("email")}
        className={inputCls}
        data-testid={`${testIdPrefix}-email-input`}
        aria-label="Email"
      />
      <input
        required
        type="tel"
        placeholder="Phone"
        value={form.phone}
        onChange={set("phone")}
        className={inputCls}
        data-testid={`${testIdPrefix}-phone-input`}
        aria-label="Phone"
      />
      <div className="relative">
        <select
          value={form.callback}
          onChange={set("callback")}
          className={`${inputCls} appearance-none pr-10 cursor-pointer`}
          data-testid={`${testIdPrefix}-callback-select`}
          aria-label="Preferred callback time"
        >
          {CALLBACK_OPTIONS.map((o) => (
            <option key={o} value={o} className="text-[#111111] bg-white">
              {o}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className={`pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 ${dark ? "text-[#F9F8F6]/50" : "text-[#999]"}`}
        />
      </div>
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full bg-[#FF3B30] text-white text-sm font-bold uppercase tracking-[0.15em] py-4 transition-[background-color,transform] duration-200 hover:bg-[#E0352B] hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
        data-testid={`${testIdPrefix}-submit-button`}
      >
        {status === "loading" ? "Sending…" : "Send my breakdown"}
      </button>
      <p className={`text-[11px] ${dark ? "text-[#F9F8F6]/40" : "text-[#999]"}`}>
        No spam, no obligation. Just your numbers and one call.
      </p>
    </form>
  );
}
